import { useEffect, useRef, useState } from "react";
import { hidePreload } from "../preload";
import { CallDetector, createAnalyzeResult, getCurrentDetector, uploadAnalyzedAudio } from "./service";
import { EasyTelecomTelephonyService } from "../components/telephony/EasyTelecomTelephonyService/EasyTelecomTelephonyService";
import { Button } from "../components/core/Button/Button";
import { isSilence } from "./utils";
import { boolean } from "boolean";
import { text } from "../utils/i18n";
import { SwitchField } from "../components/core/Switch/SwitchField";
import { configBase } from "../utils/theme";
import { sleep } from "../utils";
import { TelephonyServiceCallInfo } from "../components/telephony/TelephonyService";
import ErrorScreenWrapper from "../components/core/error_screen_wrapper";

//  Ljudfilen vi 'tränar på' måste vara en samtalsinspelning.
//  Samtalsinspelningen kan ha andra frekvenser än om man spelar in på datorn, exempelvis.
const knownAudioUrls = new Array(11).fill(null).map((_, i) => new URL(`voicemail${i + 1}.wav?t=${Date.now()}`, document.location.origin));

/**
 * View for a headless browser which automatically detects signals in calls, using audio visualizer data.
 */
export const TelephonyDetectorView: React.FC = () => {
  //  OBS: Vid utveckling är det bäst att ha detta webbläsarfönster i fokus.
  //  Det kan påverka ljudkvalitén/uppkopplingen vilket fönster som är det.
  const visualizerContext = useRef<AudioContext>(null);
  const audioStream = useRef<MediaStream>(null);
  const audioSources = useRef<{ [id: string]: MediaElementAudioSourceNode | MediaStreamAudioSourceNode }>({});
  const analyzers = useRef<{ [id: string]: AnalyserNode }>({});
  const analyzeIntervalHandlers = useRef<{ [id: string]: any }>({});  //  Värden från kända ljud.
  const knownValues = useRef<number[][][]>([]);  //  Värden från kända ljud.
  const [knownValuesUrl, setKnownValuesUrl] = useState<string>(""); //  Så värdena kan laddas ned.
  const currentValues = useRef<number[][]>([]);  //  Värden från nuvarande ljud.
  const startTime = useRef<number>(null);
  const canSubmitResult = useRef(false);
  const analyzedDuration = useRef(0); //  Hur länge vi har sparat ner värden.
  const analyzedDurationLoggerId = useRef(null); // Handle som loggar analyzedDuration.
  const analyzedDurationLoggerStopperId = useRef(null); // Handle som stoppar analyzedDurationLoggerId.
  const timeDelta = useRef(0); // Skillnad i tid mellan requestAnimationFrame.
  const [nonce, setNonce] = useState(Date.now());
  const [isDevMode, setDevMode] = useState(boolean(localStorage.getItem('isTelDetectorDevMode')));
  const [hasDocumentInteraction, setDocumentInteraction] = useState(false); //  För audio autoplay.
  const [isLoaded, setLoaded] = useState(false);
  const playingAudio = useRef<HTMLAudioElement>(null);  //  För utveckling - ljud som spelas.
  const knownIndex = useRef(0); //  Aktuell inspelning.
  const knownPauseTimeout = useRef(null); //  Timeout handle för att automatiskt pausa inspelning.
  const recorder = useRef<MediaRecorder>(null); //  Spelar in samtalets ljud. (Telnects inspelning fungerar inte)
  const recorderData = useRef<Blob[]>([]);
  const state = useRef<CallDetector>(null);
  const currentCall = useRef<TelephonyServiceCallInfo>(null);

  const debugPlaybackRate = 1;  //  Ändra denna till 0.5 för att felsöka med hjälp av grafer.

  const analyzeInterval = 16; //  Fixerad intervall i millisekunder (requestAnimationFrame måste vi räkna ut intervall dynamiskt, opålitligt.)
  const analyzeDuration = 750 / debugPlaybackRate;  //  Hur mycket av ljudet vi ska analysera, i ms.

  useEffect(() => {
    hidePreload();

    //  Vi hämtar inspelningsdata från en fil som har exporterats härifrån tidigare.
    //  Varför vi inte genererar inspelningsdata varje gång är för att den tydligen
    //  skiljer sig lite varje gång, och vi behöver justera "maxDiff" efter varje
    //  generering.
    const url = new URL("voicemail-data.json?t=" + Date.now(), configBase);
    fetch(url.href)
      .then(response => response.json())
      .then(values => {
        knownValues.current = values;
        setLoaded(true);
      })
      .catch(error => {
        console.error("alert: Error getting voicemail data", error);

        if (isDevMode) {
          //  Läs in inspelningarna så vi kan anropa .play() direkt och sedan generera en ny fil.
          return Promise.all(knownAudioUrls.map(url => fetch(url.href)))
            .then(() => setLoaded(true));
        }
      });
  }, []);

  useEffect(() => {
    localStorage.setItem('isTelDetectorDevMode', isDevMode ? 'true' : 'false');

    if (isDevMode && !knownValues.current.length) {
      knownValues.current = JSON.parse(localStorage.getItem('telDetectorKnownValues') || '[]');
    }
  }, [isDevMode]);

  function draw(data: number[], element: HTMLCanvasElement) {
    if (!data || !isDevMode) {
      return;
    }

    const ctx = element.getContext("2d");
    let x = 0;
    let barWidth = element.width / data.length;

    ctx.clearRect(0, 0, element.width, element.height);

    for (let i = 0; i < data.length; i++) {
      let barHeight = element.height * (data[i] / 255);

      ctx.fillStyle = element.getAttribute('data-color');
      ctx.fillRect(x, element.height - barHeight / 2, barWidth, barHeight);

      x += barWidth + 1;
    }
  }

  function reset() {
    currentValues.current = [];
    startTime.current = null;
    canSubmitResult.current = true;
    analyzedDuration.current = null;

    clearTimeout(analyzedDurationLoggerId.current);
    clearTimeout(analyzedDurationLoggerStopperId.current);
  }

  function startRecordingCall() {
    if (!audioStream.current) {
      return;
    }

    try {
      recorderData.current = [];
      recorder.current = new MediaRecorder(audioStream.current);
      recorder.current.start();

      recorder.current.addEventListener('dataavailable', event => {
        console.log("Got recording chunk (%s), size: %s", typeof event.data, event.data?.size);
        recorderData.current.push(event.data);
      });
    } catch (error) {
      console.error("alert: Error starting recording call", error);
    }
  }

  async function saveCallRecording() {
    if (!recorder.current) {
      return null;
    }

    try {
      recorder.current.stop();
      recorder.current = null;

      //  Wait for dataavailable event.
      for (let i = 0; i < 20; i++) {
        if (recorder.current) {
          //  Nytt samtal har kopplats, avbryt.
          break;
        }

        if (recorderData.current.find(chunk => {
          if (!(chunk instanceof Blob)) {
            //  ??
            return true;
          }

          return chunk.size > 0;
        })) {
          break;
        }

        await sleep(100);
      }

      return new Blob(recorderData.current, { type: "audio/webm; codecs=opus" });
    } catch (error) {
      console.error("alert: Error saving call recording", error);
      return null;
    }
  }

  function onAudioData(data: number[]) {
    if (analyzedDuration.current > analyzeDuration + 200) {
      //  Fail - logga inte massa saker i onödan.
      return;
    }

    if (!canSubmitResult.current) {
      //  Håller på att skicka in.
      return;
    }

    draw(data, document.getElementById('canvas-current-live') as HTMLCanvasElement)

    analyzedDuration.current += analyzeInterval;

    currentValues.current.push(data);

    if (knownValues.current[0]) {
      //  Visar synkad inspelning.
      draw(knownValues.current[0][currentValues.current.length - 1], document.getElementById('canvas-known-live') as HTMLCanvasElement);
    }

    if (analyzedDuration.current > analyzeDuration || isDevMode) {
      let finalScore = 99999;

      //  Provat olika metoder för att jämföra ljud.
      //  Pearson corelation på bins fungerade inte - ljudet är för kort och lutningen är för lika oavsett.
      //  Vi får vara mer petig och ha flera ljud att jämföra med.

      //  En "bin" är en frekvensstapel. 
      //  1. Räkna ut score för varje bin mellan [offset, offset + analyzeDuration]
      //  2. Ta genomsnittet av score.

      //  Offset är antalet gånger vi ska jämföra värdena igen fast förskjutet ett eller flera steg.
      for (let offset = 0; offset < 1; offset++) {
        let offsetScore = 0;

        for (let segment = 0; segment < currentValues.current.length; segment++) {
          let lowestSegmentScore = 9999;

          for (let knownIndex = 0, n = knownValues.current.length; knownIndex < n; knownIndex++) {
            const known = knownValues.current[knownIndex];

            if (!known[offset + segment]) {
              if (knownIndex === n - 1) {
                console.log("Missing known data at %s!", offset + segment);
              }
              continue;
            }

            if (!currentValues.current[offset + segment]) {
              //  Detta händer ifall offset är stor.
              continue;
            }

            const segmentScore = currentValues.current[offset + segment].reduce((diff, binValue, bin) =>
              diff + Math.abs(binValue - known[offset + segment][bin]), 0);

            lowestSegmentScore = Math.min(lowestSegmentScore, segmentScore);
          }

          offsetScore += lowestSegmentScore;
        }

        if (offsetScore < finalScore) {
          finalScore = offsetScore;
        }
      }

      console.log("finalScore: %s", finalScore);

      if (analyzedDuration.current > analyzeDuration) {
        canSubmitResult.current = false;

        //  Gör detta asynkront så att får ta lite längre tid.
        if (state.current.currentCallId && currentCall.current &&
          state.current.currentCallRecipient === currentCall.current.contactPhoneNumber) {

          console.log("Submitting score %s to call %s / %s",
            finalScore,
            state.current.currentCallId,
            state.current.currentCallRecipient
          );

          createAnalyzeResult({ score: finalScore }, state.current.currentCallId);

          saveCallRecording().then(recording => {
            return uploadAnalyzedAudio(recording, state.current.currentCallId);
          }).catch(error => {
            console.error("alert: Error saving call detector analyzed audio", error);
          });

        } else {
          console.log("Not submitting score %s, not current recipient: %s / %s",
            finalScore,
            currentCall.current.contactPhoneNumber,
            state.current.currentCallRecipient
          );
        }
      }
    }

    if (playingAudio.current &&
      analyzedDuration.current > analyzeDuration &&
      !playingAudio.current.paused) {
      playingAudio.current.pause();
    }
  }

  //  Se https://github.com/mdn/dom-examples/blob/main/media/web-dictaphone/scripts/app.js
  async function createAnalyzer(elementOrStream: HTMLMediaElement | MediaStream, record = false) {
    startTime.current = null;
    currentValues.current = [];

    if (visualizerContext.current) {
      //  Denna gör bara att man inte kan starta om ljud.
      //await visualizerContext.current.close();
    }

    visualizerContext.current = new AudioContext();

    const isNewSource = !audioSources.current[elementOrStream.id];
    const source = audioSources.current[elementOrStream.id] = audioSources.current[elementOrStream.id] ?? (
      elementOrStream instanceof HTMLAudioElement ?
        visualizerContext.current.createMediaElementSource(elementOrStream) :
        visualizerContext.current.createMediaStreamSource(elementOrStream as MediaStream)
    );

    const isNewAnalyzer = !analyzers.current[elementOrStream.id];
    const analyzer = analyzers.current[elementOrStream.id] = (
      analyzers.current[elementOrStream.id] ?? visualizerContext.current.createAnalyser()
    );

    if (isNewAnalyzer) {
      analyzer.fftSize = 32;
      analyzer.maxDecibels = -20; //  Default är -10, och första stapeln slår i taket fort då.
      analyzer.smoothingTimeConstant = 0.8; //  Glöm inte denna. 0.8 är default. Det verkar dock inte bli bättre att justera den.

      source.connect(analyzer);
    }

    if (isNewSource && elementOrStream instanceof HTMLAudioElement) {
      //  MediaStream gör denna att man hör ljudet 2 gånger.
      source.connect(visualizerContext.current.destination);
    }

    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    function analyzeAudio(timestamp: DOMHighResTimeStamp = 0) {
      if (timestamp && startTime.current === null) {
        startTime.current = timestamp;

      } else if (startTime.current && !timeDelta.current) {
        timeDelta.current = timestamp - startTime.current;
      }

      if (!visualizerContext.current) {
        return;
      }

      analyzer.getByteFrequencyData(dataArray);

      //  isSilence är viktig, för vi vill bara kolla på ljuddata som är 'högt'.
      if (!isSilence([...dataArray], record ? knownValues.current[knownIndex.current] ?? [] : currentValues.current)) {
        if (record) {
          if (knownPauseTimeout.current) {
            knownValues.current[knownIndex.current] = knownValues.current[knownIndex.current] ?? [];
            knownValues.current[knownIndex.current].push([...dataArray]);

            draw([...dataArray], document.getElementById('canvas-known-live') as HTMLCanvasElement);
          }

        } else {
          onAudioData([...dataArray]);
        }
      }

      if (elementOrStream instanceof HTMLAudioElement && elementOrStream.paused) {
        clearInterval(analyzeIntervalHandlers.current[elementOrStream.id]);
      }
    }

    analyzeIntervalHandlers.current[elementOrStream.id] =
      setInterval(analyzeAudio, analyzeInterval);
  }

  const isRecordingFinished = knownValues.current.length >= knownAudioUrls.length;

  useEffect(() => {
    if (isRecordingFinished && isDevMode) {
      setKnownValuesUrl(
        URL.createObjectURL(
          new Blob([JSON.stringify(knownValues.current)], { type: "application/json" })
        )
      );
    }

    if (isRecordingFinished && isDevMode) {
      window.localStorage.setItem('telDetectorKnownValues', JSON.stringify(knownValues.current));
    }
  }, [isRecordingFinished]);

  if (!hasDocumentInteraction && !isLoaded) {
    //  Så att headless kan klicka på knappen.
    return null;
  }

  if (!hasDocumentInteraction) {
    //  Interaktion fixar "NotAllowedError: play() failed because the user didn't interact with the document first".
    return (
      <ErrorScreenWrapper>
        <div>
          <div>
            <SwitchField
              before
              checked={isDevMode}
              onChange={() => setDevMode(enabled => !enabled)}
              label={text`Utvecklarläge`}
            />
          </div>
          <div>
            <Button id="detector-start" onClick={() => setDocumentInteraction(true)}>
              Starta
            </Button>
          </div>
          <div>
            <Button id="detector-start-without-cache" onClick={() => {
              //  Kommer låta oss ladda ner en fil med inspelningsdata.
              knownValues.current = [];
              setDocumentInteraction(true);
            }}>
              Starta och läs in inspelningar på nytt
            </Button>
          </div>
        </div>
      </ErrorScreenWrapper>
    )
  }

  return (
    <ErrorScreenWrapper>
      <SwitchField
        before
        checked={isDevMode}
        onChange={() => setDevMode(enabled => !enabled)}
        label={text`Utvecklarläge`}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {
          isRecordingFinished ? (
            <EasyTelecomTelephonyService
              _ref={() => { }}
              onCallConnected={info => {
                currentCall.current = info;
                getCurrentDetector().then(result => state.current = result);

                console.log("onCallConnected", info)
                reset();
                analyzedDuration.current = 0;
                startRecordingCall();

                analyzedDurationLoggerId.current = setInterval(() => {
                  console.log("analyzedDuration %s / %s: %s", state.current?.currentCallId, info.contactPhoneNumber, analyzedDuration.current);
                }, 100);

                //  Stoppa loggning av analyzedDuration.
                analyzedDurationLoggerStopperId.current = setTimeout(() => {
                  clearInterval(analyzedDurationLoggerId.current);
                }, analyzeDuration * 2);
              }}
              onCallConnecting={info => {
                currentCall.current = info;
                getCurrentDetector().then(result => state.current = result);
                console.log("onCallConnecting")
              }}
              onCallDisconnected={info => {
                console.log("onCallDisconnected", info)
                currentValues.current = [];
              }}
              onCallError={() => { }}
              onServiceConnected={() => { }}
              onRemoteStream={stream => {
                audioStream.current = stream;
                createAnalyzer(stream);
              }}
              onServiceDisconnected={() => { }}
              onServiceError={() => { }}
              onServiceInitialized={() => { }}
            />
          ) : isDevMode ? knownAudioUrls.map((url, index) => (
            <audio
              //  Graf över diff
              key={url.href}
              src={url.href}
              id={`known-${index}`}
              style={{ opacity: 0.5 }}
              controls
              crossOrigin="anonymous"
              ref={element => {
                if (!element || knownValues.current[knownIndex.current]?.length) {
                  return;
                }

                element.playbackRate = debugPlaybackRate;

                if (knownIndex.current === index && !knownPauseTimeout.current) {
                  console.log("Playing %s", element.id);
                  element.play();

                  //  Eftersom inkommande ljudet kan börja mitt i så vill vi ha data
                  //  längre än vad vi kommer analysera.
                  knownPauseTimeout.current = setTimeout(() => {
                    console.log("Pausing %s", element.id);
                    element.pause();

                    knownPauseTimeout.current = null;
                    knownIndex.current++;
                    console.log("knownIndex is now %s", knownIndex.current);

                    if (knownValues.current.find(element => !element)) {
                      alert("knownValues har ett null element vid " + knownValues.current.findIndex(element => !element));
                    }

                    setNonce(Date.now()); //  Spela nästa ljud.
                  }, analyzeDuration * 2);
                }
              }}
              onPlay={event => {
                createAnalyzer(event.currentTarget, true);
              }}
            />
          )) : null
        }
        {
          isDevMode ? (
            <>
              <p style={{ fontSize: 13, color: 'var(--color-text-3)', gridColumn: '1 / span 2' }}>
                OBS. Du får tydligen bättre värden genom att faktiskt ringa och koppla ett samtal.<br /><br />
                Startar man om en ljudfil så kan det bli missvisande värden p.g.a. analyzer.smoothingTimeConstant.
              </p>

              {
                ["/telenor/test1.wav", "/telenor/test2.wav", "/telenor/test3.wav", "/ej_rb1.wav", "/ej_rb2.wav", "/ej_rb3.wav", "/ej_rb4.wav"].map(file => (
                  //  OBS spelar man om samma ljud igen så kan det bli missvisande värden pga. smoothingTimeConstant
                  <div>
                    <p style={{ fontSize: 13, color: 'var(--color-text-3)', marginLeft: 24 }}>{file}</p>
                    <audio
                      ref={element => {
                        if (element) {
                          element.playbackRate = debugPlaybackRate;
                        }
                      }}
                      id={file}
                      src={file}
                      controls
                      crossOrigin="anonymous"
                      onPlay={event => {
                        event.currentTarget.currentTime = 0;
                        reset();

                        playingAudio.current = event.currentTarget;
                        createAnalyzer(event.currentTarget, false);
                      }}
                    />
                  </div>
                ))
              }
            </>
          ) : null
        }
      </div>

      {
        isDevMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <a download="voicemail-data.json"
              href={knownValuesUrl}>
              <Button
                icon="file"
                disabled={!knownValuesUrl}>
                Ladda ner JSON
              </Button>
            </a>

            <div style={{ fontSize: 13, color: 'var(--color-text-3)' }}>
              Ladda ner filen och lägg den i S3/salesys-config.
            </div>
          </div>
        ) : null
      }

      {
        isDevMode ? (
          <div style={{ position: 'relative' }}>
            <canvas
              id="canvas-known-live"
              data-color="green"
              width="500"
              height="300"
            />

            <canvas
              id="canvas-known-average-before-normalize"
              style={{ position: 'absolute', left: 0, top: 0 }}
              data-color="darkred"
              width="500"
              height="300"
            />

            <canvas
              id="canvas-known-average"
              style={{ position: 'absolute', left: 0, top: 0 }}
              data-color="red"
              width="500"
              height="300"
            />

            <canvas
              style={{ position: 'absolute', left: 0, top: 0, opacity: 0.5 }}
              id="canvas-current-live"
              data-color="blue"
              width="500"
              height="300"
            />

            {
              //  Nuvarande differens.
              <canvas
                style={{ position: 'absolute', left: 0, top: 0, opacity: 0.5 }}
                id="canvas-current-diff"
                data-color="pink"
                width="500"
                height="300"
              />
            }
          </div>
        ) : null
      }
    </ErrorScreenWrapper>
  )
}